import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Corporation } from '../../interfaces/corporations/corporation';
import { CorporationForCreation } from '../../interfaces/corporations/corporation-for-creation';
import { CorporationForUpdate } from '../../interfaces/corporations/corporation-for-update';
import { PaginatedRepository } from '../../services/pagination/paginated.repository';
import { SelectOption } from '../../interfaces/select-option';
import { ToastrService } from 'ngx-toastr';
import { REG_HUB_COMMON_CONFIG, RegHubCommonConfig } from '../../reg-hub-common-config';
import { DocumentBinary } from '../../interfaces/documents/document-binary';
import { Brand } from '../../interfaces/brands/brand';
import { BrandForCreation } from '../../interfaces/brands/brand-for-creation';
import { BrandForUpdate } from '../../interfaces/brands/brand-for-update';

@Injectable({
  providedIn: 'root'
})
export class CorporationRepositoryService extends PaginatedRepository<Corporation> {

  protected override _baseLink: string;

  constructor(
    http: HttpClient,
    messageService: ToastrService,
    @Inject(REG_HUB_COMMON_CONFIG) private config: RegHubCommonConfig) {
    super(
      http,
      messageService);

    this._baseLink = `${config.rootUrl}/corporations`;
  }

  private generateHeaders = () => {
    return {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    }
  }

  public getCorporation = (corporationID: string) => {
    return this.http.get<Corporation>(`${this._baseLink}/${corporationID}`);
  }

  public getCorporations(): Observable<Corporation[]> {
    return this.http.get<Corporation[]>(this._baseLink);
  }

  public createCorporation = (corporationForCreation: CorporationForCreation) => {
    return this.http.post<Corporation>(
      this._baseLink,
      corporationForCreation,
      this.generateHeaders());
  }

  public updateCorporation = (corporationID: string, corporation: CorporationForUpdate) => {
    return this.http.put(`${this._baseLink}/${corporationID}`, corporation, this.generateHeaders());
  }

  // public deleteCorporation = (corporationID: string) => {
  //   return this.http.delete(`${this._baseLink}/${corporationID}`);
  // }

  public getCorporationsAsSelectOptions(): Observable<SelectOption[]> {
    return this.http.get<SelectOption[]>(`${this._baseLink}/SelectOptions`);
  }

  public getUserGroupCorporationName = (userGroupID: string) => {
    return this.http.get<Corporation>(`${this._baseLink}/Name/UserGroups/${userGroupID}`);
  }

  public getDocument(corporationID: string): Observable<DocumentBinary> {
    const params = new HttpParams().set('hideErrorsStatusCodes', '404');
    return this.http.get<DocumentBinary>(`${this._baseLink}/${corporationID}/LogoDocumentBinary`, { params });
  }

  public getBrands(corporationID: string): Observable<Brand[]> {
    const params = new HttpParams().set('pageSize', 100);
    return this.http.get<Brand[]>(`${this._baseLink}/${corporationID}/Brands`, { params });
  }

  public getBrand(corporationID: string, brandID: string): Observable<Brand> {
    const params = new HttpParams().set('hideErrorsStatusCodes', '404');
    return this.http.get<Brand>(`${this._baseLink}/${corporationID}/Brands/${brandID}`, { params });
  }

  public createBrand(corporationID: string, brand: BrandForCreation): Observable<Brand> {
    return this.http.post<Brand>(`${this._baseLink}/${corporationID}/Brands`, brand, this.generateHeaders());
  }

  public updateBrand(corporationID: string, brandID: string, brand: BrandForUpdate): Observable<Object> {
    return this.http.put(`${this._baseLink}/${corporationID}/Brands/${brandID}`, brand, this.generateHeaders());
  }

  public deleteBrand(corporationID: string, brandID: string): Observable<Object> {
    return this.http.delete(`${this._baseLink}/${corporationID}/Brands/${brandID}`);
  }

  public getBrandDocument(corporationID: string, brandID: string): Observable<DocumentBinary> {
    const params = new HttpParams().set('hideErrorsStatusCodes', '404');
    return this.http.get<DocumentBinary>(`${this._baseLink}/${corporationID}/Brands/${brandID}/LogoDocumentBinary`, { params });
  }
}